import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { IoArrowBack } from "react-icons/io5";

export default function CommunityGuidelines({ embedded = false } = {}) {
    const navigate = useNavigate();
    const pageRootRef = useRef(null);
    const contentRef = useRef(null);

    useEffect(() => {
        if (contentRef.current) {
            contentRef.current.scrollTop = 0;
        }
    }, []);

    const scrollToSection = (sectionId) => {
        const panel = contentRef.current;
        const element = panel?.querySelector(`#${sectionId}`);
        if (!panel || !element) return;
        const offset = 16;
        const isScrollable = panel.scrollHeight > panel.clientHeight && /auto|scroll/.test(window.getComputedStyle(panel).overflowY);
        if (isScrollable) {
            panel.scrollTo({ top: element.offsetTop - offset, behavior: "smooth" });
            return;
        }

        let container = panel.parentElement;
        while (container && container !== document.documentElement) {
            const { overflow, overflowY } = window.getComputedStyle(container);
            if (/auto|scroll/.test(overflow + overflowY) && container.scrollHeight > container.clientHeight) {
                const offsetPosition = element.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop - offset;
                container.scrollTo({ top: offsetPosition, behavior: "smooth" });
                return;
            }
            container = container.parentElement;
        }

        const offsetPosition = element.getBoundingClientRect().top + window.pageYOffset - offset;
        window.scrollTo({ top: offsetPosition, behavior: "smooth" });
    };

    const tocItems = [
        { id: 1, dec: "Welcome to Chaa Chingg" },
        { id: 2, dec: "Where These Guidelines Apply" },
        { id: 3, dec: "Safety of Minors" },
        { id: 4, dec: "Violence and Criminal Behavior" },
        { id: 5, dec: "Violent and Hateful Organizations" },
        { id: 6, dec: "Hate Speech and Hateful Behavior" },
        { id: 7, dec: "Harassment and Bullying" },
        { id: 8, dec: "Suicide, Self-Harm, and Disordered Eating" },
        { id: 9, dec: "Dangerous Activities and Challenges" },
        { id: 10, dec: "Graphic and Disturbing Content" },
        { id: 11, dec: "Nudity and Sexual Content" },
        { id: 12, dec: "Human Exploitation" },
        { id: 13, dec: "Privacy, Personal Information, and Doxxing" },
        { id: 14, dec: "Local and Neighborhood Safety" },
        { id: 15, dec: "Direct Messages and Private Communications" },
        { id: 16, dec: "Regulated Goods and Services" },
        { id: 17, dec: "Fraud, Scams, and Deceptive Practices" },
        { id: 18, dec: "Misinformation That May Cause Serious Harm" },
        { id: 19, dec: "AI-Generated and Manipulated Content" },
        { id: 20, dec: "Authenticity and Account Integrity" },
        { id: 21, dec: "Platform Security" },
        { id: 22, dec: "Intellectual Property" },
        { id: 23, dec: "Recommendation and Distribution Standards" },
        { id: 24, dec: "Context and Public-Interest Exceptions" },
        { id: 25, dec: "Enforcement" },
        { id: 26, dec: "Reporting and Blocking" },
        { id: 27, dec: "Appeals" },
        { id: 28, dec: "Cooperation With Authorities" },
        { id: 29, dec: "No Guarantee of Monitoring" },
        { id: 30, dec: "Changes to These Guidelines" },
        { id: 31, dec: "Contact Us" },
    ];

    const guidingPrinciplesList = [
        "Protect the safety and dignity of our users.",
        "Provide stronger protections for minors.",
        "Support creativity, expression, and legitimate discussion.",
        "Prevent offline harm and exploitation.",
        "Encourage authentic and respectful participation.",
        "Protect the integrity and security of the platform.",
        "Apply our rules fairly and consistently.",
    ];

    const guidelinesApplyList = [
        "Videos, photographs, and other uploaded media;",
        "Captions, hashtags, comments, and replies;",
        "Usernames, display names, biographies, and profile images;",
        "Original audio, reused sounds, and other audio content;",
        "Direct Messages and other private communications;",
        "Community discussions;",
        "National, Local, and Neighborhood features;",
        "Search results and recommendations;",
        "Links, advertisements, promotions, and commercial content;",
        "Reports, appeals, and communications with Chaa Chingg; and",
        "Conduct that occurs outside Chaa Chingg when it creates a credible risk to users or the platform.",
    ];

    const minorSexualExploitationList = [
        "Child sexual abuse material or any sexual depiction of a minor;",
        "Content that sexualizes, fetishizes, or sexually objectifies a minor;",
        "Grooming or attempts to establish a relationship with a minor for sexual purposes;",
        "Requests for nude, partially nude, or sexually suggestive images from a minor;",
        "Sexual comments, fantasies, jokes, or propositions directed toward or involving minors;",
        "Sextortion, including threats to publish intimate material unless money, images, or other demands are provided;",
        "Content promoting or facilitating child trafficking or sexual exploitation;",
        "Instructions for obtaining, creating, hiding, or distributing exploitative material;",
        "Offering money, gifts, opportunities, or other benefits in exchange for sexual conduct or sexual material from a minor;",
        "Adults attempting to pursue romantic or sexual relationships with minors through Chaa Chingg; or",
        "Links or directions to websites, accounts, applications, or groups containing child sexual exploitation material.",
    ];

    const minorPersonalInfoList = [
        "A home address or precise live location;",
        "School schedules, travel routines, or frequently visited locations;",
        "Personal phone numbers or private email addresses;",
        "Passwords or account access information;",
        "Government identification numbers;",
        "Financial information; or",
        "Information that could be used to locate, stalk, exploit, or impersonate the minor.",
    ];

    const violenceList = [
        "Credible threats to kill, injure, kidnap, or sexually assault another person;",
        "Requests or offers to commit violence;",
        "Encouraging others to attack an identifiable person or group;",
        "Instructions that meaningfully facilitate violent criminal activity;",
        "Celebrating or praising a recent act of serious violence in a manner that encourages further harm;",
        "Coordinating violent attacks or criminal activity;",
        "Threatening to damage a person's home, workplace, school, business, vehicle, or property;",
        "Promoting school shootings, mass violence, terrorism, or targeted attacks;",
        "Providing operational assistance to a person planning an attack; or",
        "Content intended to intimidate victims or witnesses of violence.",
    ];

    const violentOrgList = [
        "Recruit for or materially support a violent or hateful organization;",
        "Praise, glorify, or celebrate violent attacks carried out by such an organization;",
        "Raise or transfer funds for violent activities;",
        "Distribute propaganda intended to radicalize or recruit;",
        "Share operational instructions, manifestos, or materials intended to inspire attacks;",
        "Represent themselves as an official member or spokesperson for a prohibited organization; or",
        "Promote symbols, slogans, or imagery for the purpose of advancing a violent or hateful ideology.",
    ];

    const protectedCharacteristicsList = [
        "Race or ethnicity;",
        "National origin;",
        "Religion or religious affiliation;",
        "Caste;",
        "Sex;",
        "Gender;",
        "Gender identity or expression;",
        "Sexual orientation;",
        "Disability;",
        "Serious disease or medical condition;",
        "Immigration status; and",
        "Other comparable characteristics protected under applicable law.",
    ];

    const hatefulBehaviorList = [
        "Calling for violence, exclusion, segregation, or discrimination;",
        "Describing a protected group as less than human, diseased, criminal, or inherently inferior;",
        "Claiming that one protected group is naturally superior to another;",
        "Using degrading slurs to attack or intimidate someone;",
        "Expressing hatred toward all members of a protected group;",
        "Praising hateful ideologies or historical atrocities;",
        "Denying or minimizing well-documented mass atrocities for the purpose of attacking victims;",
        "Promoting conversion practices intended to change a person's sexual orientation or gender identity through coercion or abuse; or",
        "Targeting someone with hateful abuse because of their association with a protected group.",
    ];

    const harassmentList = [
        "Threats or wishes of serious physical harm or death;",
        "Repeated unwanted contact after someone has asked for it to stop;",
        "Encouraging a group of users to harass or overwhelm another person;",
        "Sexual harassment or unwanted sexual comments;",
        "Degrading attacks concerning someone's body, disability, sexual history, trauma, or private life;",
        "Celebrating or mocking a person's death, serious injury, or victimization;",
        "Harassing the family members of a victim;",
        "Coordinating false reports against an account;",
        "Threatening to reveal private information or intimate material;",
        "Targeting minors with humiliating, threatening, or sexually suggestive comments;",
        "Impersonating someone for the purpose of humiliating or harming them; or",
        "Using edited media to falsely portray a private person in a degrading or sexual situation.",
    ];

    const selfHarmList = [
        "Encouraging or instructing another person to die by suicide or harm themselves;",
        "Detailed instructions for suicide or self-injury;",
        "Content that glorifies, romanticizes, or presents self-harm as desirable;",
        "Suicide games, hoaxes, pacts, or challenges;",
        "Graphic depictions of self-inflicted injuries;",
        "Requests for methods, locations, or tools for suicide or self-harm;",
        "Promoting eating disorders or dangerous weight-loss behaviors;",
        "Encouraging purging, starvation, extreme fasting, or deliberate misuse of medication;",
        "Competitive content based on extreme thinness, self-harm, or disordered eating; or",
        "Mocking or targeting someone because of a mental-health crisis.",
    ];

    const dangerousActivitiesList = [
        "Dangerous challenges, dares, games, or stunts;",
        "Choking, blackout, suffocation, or ingestion challenges;",
        "Reckless driving, street racing, or dangerous use of a vehicle;",
        "Improper use of firearms, explosives, fireworks, or dangerous tools;",
        "Instructions for manufacturing bombs or other weapons intended to harm people;",
        "Dangerous climbing, jumping, or trespassing without appropriate safety precautions;",
        "Misuse of drugs, household chemicals, or medication;",
        "Content encouraging minors to participate in dangerous conduct; or",
        "Attempts to imitate dangerous behavior that has resulted in serious injury or death.",
    ];

    const graphicContentList = [
        "Graphic images of severe injuries, mutilation, or death;",
        "Visible human remains or exposed internal organs;",
        "Torture, executions, or violent deaths;",
        "Graphic crime-scene footage;",
        "Severe animal abuse or cruelty;",
        "Content celebrating a person's suffering; or",
        "Gore presented for entertainment or shock value.",
    ];

    const nudityList = [
        "Sexual intercourse, masturbation, or other explicit sexual activity;",
        "Visible genitals or explicit close-ups of intimate body parts;",
        "Pornographic or fetish content;",
        "Sexual solicitation or offers to buy or sell sexual services;",
        "Requests for nude or explicit images;",
        "Non-consensual intimate imagery;",
        "Threats to share intimate imagery;",
        "Voyeuristic or hidden-camera sexual content;",
        "Sexual deepfakes or manipulated intimate imagery;",
        "Content depicting sexual violence;",
        "Links to pornographic websites or services; or",
        "Content designed primarily for sexual arousal.",
    ];

    const humanExploitationList = [
        "Human trafficking;",
        "Forced labor or domestic servitude;",
        "Sexual exploitation;",
        "Smuggling people for exploitative purposes;",
        "Selling or purchasing a person;",
        "Recruiting people through deceptive employment or modeling offers;",
        "Coercing someone through threats, debt, immigration status, or control of identification documents; or",
        "Sharing content that identifies or retaliates against a victim of trafficking or abuse.",
    ];

    const privacyDoxxingList = [
        "Home addresses or precise private locations;",
        "Live location or private travel information;",
        "Personal phone numbers or private email addresses;",
        "Passwords, security codes, or account credentials;",
        "Government identification numbers or documents;",
        "Banking, payment-card, or financial account information;",
        "Private medical, employment, or educational records;",
        "Intimate images or recordings;",
        "Information identifying protected witnesses or confidential informants;",
        "Information that could expose someone to stalking, theft, harassment, or physical harm; or",
        "Personal information obtained through hacking, deception, or unauthorized access.",
    ];

    const localSafetyList = [
        "Use local features to stalk, track, or monitor another person;",
        "Publish someone's home address to encourage confrontation or harassment;",
        "Organize vigilante activity or ask users to confront an alleged suspect;",
        "Falsely claim that an identifiable person committed a serious crime;",
        "Post unverified accusations likely to create an immediate risk of violence;",
        "Crowdsource the identity of a private individual shown in a photograph or video;",
        "Post a minor's school, routine, or live location;",
        "Create false emergency, crime, evacuation, or public-safety alerts;",
        "Impersonate police officers, emergency personnel, public agencies, or local officials;",
        "Encourage trespassing or entry onto private property;",
        "Use location information to threaten or intimidate someone; or",
        "Advertise prohibited goods or services to nearby users.",
    ];

    const directMessagesList = [
        "Threats, harassment, or repeated unwanted messages;",
        "Unsolicited sexual content;",
        "Requests for nude or sexual images;",
        "Grooming or exploitative messages involving minors;",
        "Scams, phishing links, or malicious files;",
        "Hate speech;",
        "Private information belonging to another person;",
        "Instructions for violence, self-harm, or serious criminal activity; or",
        "Messages intended to evade enforcement by moving prohibited conduct off-platform.",
    ];

    const regulatedGoodsList = [
        "Illegal drugs;",
        "Controlled substances sold without lawful authorization;",
        "Prescription medication sold without proper authorization;",
        "Firearms, ammunition, explosives, or prohibited weapons;",
        "Tobacco, nicotine, or vaping products offered to minors;",
        "Alcohol offered to minors;",
        "Counterfeit identification documents;",
        "Stolen goods;",
        "Human organs or body parts;",
        "Endangered wildlife or illegal wildlife products;",
        "Sexual services;",
        "Instructions or materials primarily intended to manufacture illegal drugs or explosives; or",
        "Other products or services whose sale is prohibited by applicable law.",
    ];

    const fraudList = [
        "Phishing or attempts to steal passwords and security codes;",
        "Fake giveaways, prizes, employment offers, or investment opportunities;",
        "Romance scams;",
        "Impersonating a business, government agency, charity, or public figure to obtain money or information;",
        "Fraudulent fundraising;",
        "Pyramid schemes and deceptive income opportunities;",
        "Selling counterfeit, stolen, or nonexistent goods;",
        "Payment fraud or unauthorized financial transactions;",
        "Manipulating users into installing malware or unsafe software;",
        "Deceptive links that hide their true destination;",
        "False claims of emergencies intended to obtain money; or",
        "Instructions designed to help others commit fraud or identity theft.",
    ];

    const misinformationList = [
        "False information about when, where, how, or whether someone may vote;",
        "Content intended to intimidate people from voting or participating in an election;",
        "False emergency, evacuation, weather, crime, or public-health alerts;",
        "Dangerous medical claims that discourage urgent or necessary care;",
        "False instructions likely to cause poisoning, injury, or death;",
        "Manipulated media falsely showing a person committing or admitting to serious misconduct;",
        "Fabricated content impersonating an official government or emergency source;",
        "False claims intended to trigger violence against an identifiable person or community; or",
        "Coordinated deceptive campaigns intended to manipulate public discussion.",
    ];

    const aiContentList = [
        "Sexually depicts a real person without consent;",
        "Sexualizes or exploits a minor;",
        "Impersonates another person for fraud or harassment;",
        "Falsely depicts a serious crime, emergency, or violent event as authentic;",
        "Creates a credible false confession or endorsement;",
        "Misleads users about voting or election procedures;",
        "Is used for extortion, identity theft, or financial fraud; or",
        "Violates another section of these Guidelines.",
    ];

    const accountIntegrityList = [
        "Impersonating another person, business, organization, or government agency in a deceptive manner;",
        "Creating accounts intended to mislead users about their source or purpose;",
        "Selling, buying, or transferring accounts without authorization;",
        "Using bots or scripts to create fake engagement;",
        "Buying or selling followers, likes, comments, views, or shares;",
        "Coordinating fake reviews or deceptive engagement;",
        "Operating account networks to manipulate recommendations or public discussion;",
        "Posting repetitive, irrelevant, or mass-produced spam;",
        "Creating new accounts to evade a suspension or permanent ban;",
        "Concealing the source of political, commercial, or coordinated content;",
        "Using another person's images or identity to conduct a scam; or",
        "Making false claims that an account has been verified or endorsed by Chaa Chingg.",
    ];

    const platformSecurityList = [
        "Hacking or attempting to bypass security measures;",
        "Distributing malware, spyware, ransomware, or credential-stealing tools;",
        "Stealing or attempting to obtain account credentials;",
        "Scraping data without authorization;",
        "Accessing private APIs, systems, or databases without permission;",
        "Testing vulnerabilities in a manner that risks user data or platform availability;",
        "Sharing stolen Chaa Chingg data;",
        "Conducting denial-of-service attacks;",
        "Reverse engineering or manipulating systems in violation of Chaa Chingg's Terms;",
        "Interfering with content moderation or reporting systems;",
        "Using automated tools to create accounts, post content, or manipulate engagement without authorization; or",
        "Encouraging others to compromise the platform.",
    ];

    const intellectualPropertyList = [
        "Upload copyrighted videos, photographs, music, television programs, films, artwork, or other material without authorization;",
        "Misuse trademarks in a way that confuses users about sponsorship or affiliation;",
        "Sell or distribute pirated material;",
        "Remove copyright-management information;",
        "Use Chaa Chingg to direct users to illegal downloads or streams; or",
        "Repeatedly infringe the intellectual-property rights of others.",
    ];

    const recommendationStandardsList = [
        "Sexually suggestive or mature themes;",
        "Non-graphic but disturbing imagery;",
        "Dangerous behavior that could be imitated;",
        "Depictions of alcohol, tobacco, drugs, or weapons;",
        "Strong or repeated profanity;",
        "Unverified claims concerning developing emergencies or public-safety incidents;",
        "Content that sensationalizes crime, injury, or conflict;",
        "Spam, engagement bait, or repetitive low-quality content;",
        "Content that borders on harassment or degradation;",
        "Material that may be unsuitable for Teen Accounts; or",
        "Other content that may be lawful and permitted but inappropriate for broad recommendation.",
    ];

    const publicInterestList = [
        "Educational value;",
        "Documentary value;",
        "Medical or scientific value;",
        "Artistic value;",
        "News or public-interest value;",
        "Historical value;",
        "Counterspeech or awareness value; or",
        "Recovery or prevention value.",
    ];

    const enforcementActionsList = [
        "Prevent content from being uploaded or delivered;",
        "Remove content;",
        "Add a warning or informational notice;",
        "Limit visibility or recommendation eligibility;",
        "Restrict content from Teen Accounts;",
        "Disable comments, messaging, posting, or other features;",
        "Remove a user from a Group;",
        "Temporarily suspend an account;",
        "Permanently ban an account;",
        "Prevent the creation of replacement accounts;",
        "Restrict devices or technical identifiers associated with serious abuse;",
        "Preserve relevant account information or content;",
        "Report activity to law enforcement, emergency services, or appropriate organizations; or",
        "Take other reasonable action to protect users and the platform.",
    ];

    const enforcementFactorsList = [
        "The severity of the conduct;",
        "Whether a minor was involved;",
        "Whether there is a risk of offline harm;",
        "Whether the violation was intentional;",
        "The context in which the content was shared;",
        "The user's previous violations;",
        "Whether the conduct was repeated or coordinated;",
        "Whether the user attempted to evade enforcement; and",
        "Requirements imposed by applicable law.",
    ];

    const cooperationList = [
        "Comply with applicable law or valid legal process;",
        "Protect a person from death or serious physical harm;",
        "Investigate child exploitation, trafficking, terrorism, or serious violence;",
        "Detect or prevent fraud and cybersecurity threats;",
        "Enforce our Terms and these Guidelines; or",
        "Protect the rights, property, and safety of Chaa Chingg, our users, or others.",
    ];

    const contactList = [
        "The Chaa Chingg Company",
        "Community Safety Email: support@chaachingg.com",
        "Privacy Matters: privacy@chaachingg.com",
        <>Website: <span onClick={() => navigate("/support")} className="text-blue-600 underline hover:text-blue-800 cursor-pointer">chaachingg.com</span></>,
    ];

    const bulletList = (items) => (
        <ul className="list-disc ml-8 space-y-1.5 text-[12px] text-gray-700">
            {items.map((item, index) => (
                <li key={`bullet-${index}`}>{item}</li>
            ))}
        </ul>
    );

    return (
        <div ref={pageRootRef} className="flex flex-col h-full">
            {!embedded && (
                <button
                    type="button"
                    onClick={() => navigate(-1)}
                    className="md:hidden flex items-center gap-1.5 px-4 pt-4 text-[14px] font-semibold text-[#365975]"
                    aria-label="Go back"
                >
                    <IoArrowBack className="text-[20px]" />
                    Back
                </button>
            )}

            <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
                {!embedded && (
                    <aside className="md:w-64 md:my-5 md:shrink-0 md:border md:border-gray-300 md:bg-gray-50 md:overflow-y-auto md:rounded-xl border-b border-gray-200 bg-gray-50">
                        <div className="p-4">
                            <h2 className="text-[11px] font-bold uppercase tracking-wider text-black mb-3">
                                Community Guidelines Contents
                            </h2>
                            <nav className="flex flex-wrap md:flex-col gap-1 md:gap-0 md:space-y-0.5">
                                {tocItems.map((item) => (
                                    <button
                                        key={item.id}
                                        onClick={() => scrollToSection(`section-${item.id}`)}
                                        className="text-left text-[11px] text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded px-2 py-1 transition-colors"
                                    >
                                        {item.id}. {item.dec}
                                    </button>
                                ))}
                            </nav>
                        </div>
                    </aside>
                )}

                <main ref={contentRef} className="flex-1 overflow-y-auto px-4 md:px-8 py-6 text-justify">
                    <section className="mb-6">
                        <h1 className="text-[16px] font-bold text-gray-900">Chaa Chingg Community Guidelines</h1>
                        <p className="text-[12px] text-gray-800 font-semibold mt-2">Last Updated: August 2nd, 2026</p>
                        <p className="text-[12px] text-gray-800 font-semibold">Effective: August 3rd, 2026</p>
                    </section>

                    <hr className="border-gray-300 mb-6" />

                    <section id="section-1" className="mb-6">
                        <h2 className="text-[14px] font-bold mb-2 text-gray-800">1. Welcome to Chaa Chingg</h2>
                        <div className="space-y-2 text-gray-700 text-[12px]">
                            <p>Chaa Chingg is a social platform designed to help people create, discover, and connect with communities and content that matter to them, including content from their local area.</p>
                            <p>We want Chaa Chingg to be a place where people can express themselves, share ideas, discover what is happening around them, and build meaningful communities without being exposed to abuse, exploitation, or unnecessary harm.</p>
                            <p>These Community Guidelines explain what is and is not allowed on Chaa Chingg. They apply to everyone who uses the platform and to all content, communications, and activity on Chaa Chingg.</p>
                            <p>Our guiding principles are:</p>
                            {bulletList(guidingPrinciplesList)}
                            <p>Chaa Chingg is available only to people who are at least 13 years old. People under 13 may not create or use an account.</p>
                        </div>
                    </section>

                    <section id="section-2" className="mb-6">
                        <h2 className="text-[14px] font-bold mb-2 text-gray-800">2. Where These Guidelines Apply</h2>
                        <div className="space-y-2 text-gray-700 text-[12px]">
                            <p>These Guidelines apply to all features and areas of Chaa Chingg, including:</p>
                            {bulletList(guidelinesApplyList)}
                            <p>For purposes of these Guidelines, "content" includes anything communicated, uploaded, created, displayed, or shared through Chaa Chingg.</p>
                        </div>
                    </section>

                    <section id="section-3" className="mb-6">
                        <h2 className="text-[14px] font-bold mb-2 text-gray-800">3. Safety of Minors</h2>
                        <div className="space-y-2 text-gray-700 text-[12px]">
                            <p>Protecting minors is one of Chaa Chingg's highest priorities.</p>
                            <p>A "minor" is anyone under 18 years old, even if the legal age of adulthood is lower in a particular location.</p>
                            <h3 className="font-bold text-[12px] text-gray-800 mt-2">Sexual exploitation and abuse of minors</h3>
                            <p>Chaa Chingg does not allow:</p>
                            {bulletList(minorSexualExploitationList)}
                            <p>Do not upload nude or sexually suggestive depictions of minors, even when the person sharing the content claims that it was innocent, humorous, artistic, or shared with permission. We may remove such content to protect the person depicted.</p>
                            <p>Apparent child sexual exploitation may be preserved and reported to the National Center for Missing & Exploited Children, law enforcement, or other appropriate authorities when required or appropriate.</p>
                            <h3 className="font-bold text-[12px] text-gray-800 mt-2">Additional protections for teen accounts</h3>
                            <p>Accounts belonging to users ages 13 through 17 are treated as Teen Accounts and may receive additional privacy, safety, recommendation, messaging, and parental-control protections.</p>
                            <p>Users ages 13 through 15 do not have access to Direct Messages.</p>
                            <p>Users may not misrepresent their age to bypass a safety restriction or gain access to an age-restricted feature. Chaa Chingg may request additional age information, restrict features, suspend an account, or remove an account when we reasonably believe the user does not meet our age requirements.</p>
                            <h3 className="font-bold text-[12px] text-gray-800 mt-2">Protecting minors' personal information</h3>
                            <p>Do not post or request sensitive information about a minor, including:</p>
                            {bulletList(minorPersonalInfoList)}
                        </div>
                    </section>

                    <section id="section-4" className="mb-6">
                        <h2 className="text-[14px] font-bold mb-2 text-gray-800">4. Violence and Criminal Behavior</h2>
                        <div className="space-y-2 text-gray-700 text-[12px]">
                            <p>Chaa Chingg does not allow content or conduct that threatens, promotes, facilitates, or celebrates serious violence.</p>
                            <p>This includes:</p>
                            {bulletList(violenceList)}
                            <p>General discussion of crime, public safety, history, news, fiction, or personal experiences may be allowed when it does not encourage or facilitate harm.</p>
                            <p>When we identify a specific and credible threat, we may notify law enforcement or emergency services.</p>
                        </div>
                    </section>

                    <section id="section-5" className="mb-6">
                        <h2 className="text-[14px] font-bold mb-2 text-gray-800">5. Violent and Hateful Organizations</h2>
                        <div className="space-y-2 text-gray-700 text-[12px]">
                            <p>Chaa Chingg does not allow individuals or organizations that engage in terrorism, violent extremism, organized hate, mass violence, human trafficking, or other serious organized violence to use the platform to advance their activities.</p>
                            <p>Users may not:</p>
                            {bulletList(violentOrgList)}
                            <p>Documentary, educational, journalistic, historical, counterspeech, and critical discussion may be permitted when the context is clear and the content does not promote or facilitate violence.</p>
                        </div>
                    </section>

                    <section id="section-6" className="mb-6">
                        <h2 className="text-[14px] font-bold mb-2 text-gray-800">6. Hate Speech and Hateful Behavior</h2>
                        <div className="space-y-2 text-gray-700 text-[12px]">
                            <p>Chaa Chingg welcomes people from different communities and backgrounds. We do not allow attacks against people based on protected or vulnerable characteristics.</p>
                            <p>These characteristics include actual or perceived:</p>
                            {bulletList(protectedCharacteristicsList)}
                            <p>Prohibited hateful behavior includes:</p>
                            {bulletList(hatefulBehaviorList)}
                            <p>Discussion of hateful words, ideologies, or historical events may be allowed in educational, documentary, counterspeech, newsworthy, artistic, or self-referential contexts. Context must be clear.</p>
                        </div>
                    </section>

                    <section id="section-7" className="mb-6">
                        <h2 className="text-[14px] font-bold mb-2 text-gray-800">7. Harassment and Bullying</h2>
                        <div className="space-y-2 text-gray-700 text-[12px]">
                            <p>People may disagree, criticize ideas, debate public issues, and respond to content they dislike. They may not use Chaa Chingg to threaten, degrade, intimidate, or repeatedly target others.</p>
                            <p>We do not allow:</p>
                            {bulletList(harassmentList)}
                            <p>Public figures may be subject to criticism, parody, and commentary relating to their public actions. However, credible threats, doxxing, severe sexual harassment, hateful attacks, and coordinated abuse are prohibited regardless of who is targeted.</p>
                        </div>
                    </section>

                    <section id="section-8" className="mb-6">
                        <h2 className="text-[14px] font-bold mb-2 text-gray-800">8. Suicide, Self-Harm, and Disordered Eating</h2>
                        <div className="space-y-2 text-gray-700 text-[12px]">
                            <p>We want people to be able to discuss mental health and seek support. However, content that encourages or instructs people to harm themselves can create serious risks.</p>
                            <p>Chaa Chingg does not allow:</p>
                            {bulletList(selfHarmList)}
                            <p>Recovery stories, personal experiences, prevention information, and supportive discussions may be allowed when they do not include harmful instructions or encouragement.</p>
                            <p>Chaa Chingg may remove content, limit its distribution, provide support resources, or contact appropriate services when we reasonably believe someone faces an immediate risk of serious harm.</p>
                        </div>
                    </section>

                    <section id="section-9" className="mb-6">
                        <h2 className="text-[14px] font-bold mb-2 text-gray-800">9. Dangerous Activities and Challenges</h2>
                        <div className="space-y-2 text-gray-700 text-[12px]">
                            <p>Do not use Chaa Chingg to encourage conduct that is likely to cause serious injury or death.</p>
                            <p>Prohibited content includes:</p>
                            {bulletList(dangerousActivitiesList)}
                            <p>Professionally supervised activities, safety demonstrations, news reporting, and educational material may be permitted when adequate context and precautions are shown. Such content may still be excluded from recommendations, particularly for Teen Accounts.</p>
                        </div>
                    </section>

                    <section id="section-10" className="mb-6">
                        <h2 className="text-[14px] font-bold mb-2 text-gray-800">10. Graphic and Disturbing Content</h2>
                        <div className="space-y-2 text-gray-700 text-[12px]">
                            <p>Chaa Chingg does not allow content primarily intended to shock, disgust, traumatize, or celebrate suffering.</p>
                            <p>This includes:</p>
                            {bulletList(graphicContentList)}
                            <p>Some content may be permitted when it has clear public-interest, documentary, medical, educational, or news value. We may apply warnings, restrict access, disable recommendations, or remove the most graphic portions.</p>
                        </div>
                    </section>

                    <section id="section-11" className="mb-6">
                        <h2 className="text-[14px] font-bold mb-2 text-gray-800">11. Nudity and Sexual Content</h2>
                        <div className="space-y-2 text-gray-700 text-[12px]">
                            <p>Chaa Chingg is not a platform for pornography or sexual services.</p>
                            <p>We do not allow:</p>
                            {bulletList(nudityList)}
                            <p>Limited nudity may be considered in medical, educational, breastfeeding, childbirth, documentary, protest, or artistic contexts. The context must be clear, and the content may still be restricted from recommendations.</p>
                            <p>Sexual content involving minors is never permitted.</p>
                            <p>Sexually suggestive content that does not qualify for removal may be restricted, age-limited, or excluded from recommendations, particularly for Teen Accounts.</p>
                        </div>
                    </section>

                    <section id="section-12" className="mb-6">
                        <h2 className="text-[14px] font-bold mb-2 text-gray-800">12. Human Exploitation</h2>
                        <div className="space-y-2 text-gray-700 text-[12px]">
                            <p>Chaa Chingg does not allow content that facilitates or promotes exploitation.</p>
                            <p>This includes:</p>
                            {bulletList(humanExploitationList)}
                            <p>Content that raises awareness, seeks help, reports exploitation, or provides legitimate educational information may be allowed.</p>
                        </div>
                    </section>

                    <section id="section-13" className="mb-6">
                        <h2 className="text-[14px] font-bold mb-2 text-gray-800">13. Privacy, Personal Information, and Doxxing</h2>
                        <div className="space-y-2 text-gray-700 text-[12px]">
                            <p>Respect the privacy and safety of others.</p>
                            <p>Do not share, threaten to share, request, sell, or misuse private or sensitive information without authorization.</p>
                            <p>This includes:</p>
                            {bulletList(privacyDoxxingList)}
                            <p>Publicly advertised business addresses, event locations, and professional contact information may generally be shared for legitimate purposes.</p>
                            <p>Do not use Chaa Chingg to crowdsource the identity, address, workplace, school, routine, or live location of a private person.</p>
                        </div>
                    </section>

                    <section id="section-14" className="mb-6">
                        <h2 className="text-[14px] font-bold mb-2 text-gray-800">14. Local and Neighborhood Safety</h2>
                        <div className="space-y-2 text-gray-700 text-[12px]">
                            <p>Local information can be useful, but it can also create immediate offline risks. Users must take additional care when posting about people, homes, businesses, schools, incidents, or events in their community.</p>
                            <p>Users may not:</p>
                            {bulletList(localSafetyList)}
                            <p>When discussing suspected crimes or emergencies, avoid identifying private individuals unless the information comes from an official public source and sharing it does not create an unreasonable safety risk.</p>
                            <p>Chaa Chingg does not endorse or recommend meeting strangers through the platform. Users should exercise independent judgment and appropriate safety precautions before attending an event, visiting a location, entering a private property, or interacting with someone they encountered through Chaa Chingg.</p>
                            <p>Chaa Chingg is not an emergency service. Contact local emergency services when immediate assistance is required.</p>
                        </div>
                    </section>

                    <section id="section-15" className="mb-6">
                        <h2 className="text-[14px] font-bold mb-2 text-gray-800">15. Direct Messages and Private Communications</h2>
                        <div className="space-y-2 text-gray-700 text-[12px]">
                            <p>These Community Guidelines apply to Direct Messages and other non-public communications.</p>
                            <p>Users may not send:</p>
                            {bulletList(directMessagesList)}
                            <p>Users ages 13 through 15 do not have access to Direct Messages.</p>
                            <p>Chaa Chingg may use automated systems and, when appropriate, human review to detect prohibited content, block a message before delivery, investigate reports, protect users, and enforce these Guidelines.</p>
                            <p>A message being described as "private" does not prevent the recipient from reporting, saving, copying, recording, or sharing it.</p>
                        </div>
                    </section>

                    <section id="section-16" className="mb-6">
                        <h2 className="text-[14px] font-bold mb-2 text-gray-800">16. Regulated Goods and Services</h2>
                        <div className="space-y-2 text-gray-700 text-[12px]">
                            <p>Chaa Chingg may permit legitimate discussion of regulated goods but does not allow unauthorized or harmful transactions.</p>
                            <p>Users may not buy, sell, gift, trade, distribute, or meaningfully facilitate access to:</p>
                            {bulletList(regulatedGoodsList)}
                            <p>Content showing lawful adult use of alcohol, tobacco, firearms, or other regulated products may be restricted from recommendations or from Teen Accounts.</p>
                        </div>
                    </section>

                    <section id="section-17" className="mb-6">
                        <h2 className="text-[14px] font-bold mb-2 text-gray-800">17. Fraud, Scams, and Deceptive Practices</h2>
                        <div className="space-y-2 text-gray-700 text-[12px]">
                            <p>Do not use Chaa Chingg to deceive or financially exploit others.</p>
                            <p>Prohibited activity includes:</p>
                            {bulletList(fraudList)}
                            <p>Legitimate businesses, creators, and organizations may promote lawful products and services, but commercial relationships and paid promotions must be clearly disclosed.</p>
                        </div>
                    </section>

                    <section id="section-18" className="mb-6">
                        <h2 className="text-[14px] font-bold mb-2 text-gray-800">18. Misinformation That May Cause Serious Harm</h2>
                        <div className="space-y-2 text-gray-700 text-[12px]">
                            <p>People may express opinions, debate disputed issues, make predictions, and share personal experiences. Chaa Chingg does not remove content merely because it is unpopular or because another user believes it is incorrect.</p>
                            <p>However, we may remove or restrict false or misleading content when it creates a significant risk of physical, financial, civic, or public-safety harm.</p>
                            <p>Examples include:</p>
                            {bulletList(misinformationList)}
                            <p>Chaa Chingg may remove content, add context, reduce its distribution, make it ineligible for recommendation, or direct users to authoritative information.</p>
                            <p>Clearly labeled parody, satire, fiction, and personal opinion are generally permitted unless they independently violate another rule.</p>
                        </div>
                    </section>

                    <section id="section-19" className="mb-6">
                        <h2 className="text-[14px] font-bold mb-2 text-gray-800">19. AI-Generated and Manipulated Content</h2>
                        <div className="space-y-2 text-gray-700 text-[12px]">
                            <p>Users may create and share content made or modified with artificial intelligence, editing tools, filters, or other technology. However, users must not use these technologies to deceive, exploit, or seriously harm others.</p>
                            <p>Users should clearly disclose when realistic content has been artificially generated or materially altered in a way that could cause viewers to believe a person said or did something that did not happen.</p>
                            <p>Chaa Chingg does not allow manipulated or AI-generated content that:</p>
                            {bulletList(aiContentList)}
                            <p>Chaa Chingg may label, restrict, or remove synthetic media even when the person posting it did not create the original material.</p>
                        </div>
                    </section>

                    <section id="section-20" className="mb-6">
                        <h2 className="text-[14px] font-bold mb-2 text-gray-800">20. Authenticity and Account Integrity</h2>
                        <div className="space-y-2 text-gray-700 text-[12px]">
                            <p>Users do not always have to use their legal names, but they must not mislead others in ways that cause harm.</p>
                            <p>We do not allow:</p>
                            {bulletList(accountIntegrityList)}
                            <p>Parody, commentary, and fan accounts may be allowed when they are clearly identified and are not likely to mislead users.</p>
                        </div>
                    </section>

                    <section id="section-21" className="mb-6">
                        <h2 className="text-[14px] font-bold mb-2 text-gray-800">21. Platform Security</h2>
                        <div className="space-y-2 text-gray-700 text-[12px]">
                            <p>Do not attempt to disrupt, exploit, or gain unauthorized access to Chaa Chingg, its users, or its systems.</p>
                            <p>Prohibited conduct includes:</p>
                            {bulletList(platformSecurityList)}
                            <p>Good-faith security research should be reported through Chaa Chingg's designated security-reporting process and must not expose users to harm.</p>
                        </div>
                    </section>

                    <section id="section-22" className="mb-6">
                        <h2 className="text-[14px] font-bold mb-2 text-gray-800">22. Intellectual Property</h2>
                        <div className="space-y-2 text-gray-700 text-[12px]">
                            <p>Only upload or share content that you created, have permission to use, or are otherwise legally entitled to share.</p>
                            <p>Users may not:</p>
                            {bulletList(intellectualPropertyList)}
                            <p>Using a sound through an authorized Chaa Chingg reuse feature does not grant ownership of that sound or permission to use it outside the functionality provided by Chaa Chingg.</p>
                            <p>Rights holders may submit copyright or trademark complaints through Chaa Chingg's designated intellectual-property reporting process. Accounts belonging to repeat infringers may be suspended or permanently removed.</p>
                        </div>
                    </section>

                    <section id="section-23" className="mb-6">
                        <h2 className="text-[14px] font-bold mb-2 text-gray-800">23. Recommendation and Distribution Standards</h2>
                        <div className="space-y-2 text-gray-700 text-[12px]">
                            <p>Content being allowed on Chaa Chingg does not guarantee that it will be recommended, prominently distributed, or made available to every age group.</p>
                            <p>Chaa Chingg may exclude content from the National Feed, Local City Feed, Neighborhood recommendations, Group recommendations, Search, or other discovery surfaces when it is unsuitable for broad or unexpected audiences.</p>
                            <p>Content may be made ineligible for recommendation when it contains:</p>
                            {bulletList(recommendationStandardsList)}
                            <p>Chaa Chingg may apply stronger recommendation limits for Teen Accounts.</p>
                        </div>
                    </section>

                    <section id="section-24" className="mb-6">
                        <h2 className="text-[14px] font-bold mb-2 text-gray-800">24. Context and Public-Interest Exceptions</h2>
                        <div className="space-y-2 text-gray-700 text-[12px]">
                            <p>In some circumstances, content that would ordinarily be restricted may be allowed because it has clear:</p>
                            {bulletList(publicInterestList)}
                            <p>A caption such as "for educational purposes" does not automatically create an exception. We consider the content as a whole, including what is shown, how it is presented, who may see it, whether identities are protected, and whether the content could facilitate harm.</p>
                            <p>Exceptions generally do not apply to child sexual exploitation, credible violent threats, non-consensual intimate imagery, doxxing that creates serious danger, or content directly facilitating severe harm.</p>
                        </div>
                    </section>

                    <section id="section-25" className="mb-6">
                        <h2 className="text-[14px] font-bold mb-2 text-gray-800">25. Enforcement</h2>
                        <div className="space-y-2 text-gray-700 text-[12px]">
                            <p>Chaa Chingg may use automated systems, human review, user reports, trusted reports, and other available information to identify potential violations.</p>
                            <p>Depending on the circumstances, we may:</p>
                            {bulletList(enforcementActionsList)}
                            <p>We consider factors such as:</p>
                            {bulletList(enforcementFactorsList)}
                            <p>Chaa Chingg may permanently ban an account after a single severe violation. Severe violations may include child sexual exploitation, credible threats of serious violence, human trafficking, terrorism, non-consensual intimate imagery, large-scale fraud, or attempts to seriously compromise the platform.</p>
                            <p>Repeated lower-level violations may also result in permanent removal.</p>
                            <p>Deleting prohibited content after it has been reported does not prevent Chaa Chingg from taking action against the account.</p>
                        </div>
                    </section>

                    <section id="section-26" className="mb-6">
                        <h2 className="text-[14px] font-bold mb-2 text-gray-800">26. Reporting and Blocking</h2>
                        <div className="space-y-2 text-gray-700 text-[12px]">
                            <p>Users can report content, comments, messages, accounts, and other activity through Chaa Chingg's in-app reporting tools.</p>
                            <p>Users may also block accounts they do not want to interact with. Blocking may prevent the blocked account from viewing, contacting, following, or interacting with the user, depending on the applicable feature.</p>
                            <p>Reports should be submitted in good faith. Do not use reporting tools to harass users, suppress legitimate opinions, manipulate moderation, or retaliate against someone.</p>
                            <p>Chaa Chingg reviews reports and attempts to respond within a reasonable period based on the seriousness, complexity, and urgency of the reported conduct.</p>
                            <p>For immediate danger, contact local emergency services. Do not rely on an in-app report as a substitute for emergency assistance.</p>
                        </div>
                    </section>

                    <section id="section-27" className="mb-6">
                        <h2 className="text-[14px] font-bold mb-2 text-gray-800">27. Appeals</h2>
                        <div className="space-y-2 text-gray-700 text-[12px]">
                            <p>When Chaa Chingg removes content, restricts an account, or takes another enforcement action, we may provide the affected user with information about the rule involved and an opportunity to request another review.</p>
                            <p>Appeals may be submitted through the method identified in the enforcement notice or by contacting <span className="font-semibold">support@chaachingg.com</span>.</p>
                            <p>Submitting an appeal does not guarantee that the content or account will be restored. Chaa Chingg may decline repetitive, abusive, fraudulent, or untimely appeals.</p>
                        </div>
                    </section>

                    <section id="section-28" className="mb-6">
                        <h2 className="text-[14px] font-bold mb-2 text-gray-800">28. Cooperation With Authorities</h2>
                        <div className="space-y-2 text-gray-700 text-[12px]">
                            <p>Chaa Chingg may preserve, disclose, or report information when we reasonably believe doing so is necessary to:</p>
                            {bulletList(cooperationList)}
                            <p>Requests from government and law-enforcement authorities must follow Chaa Chingg's applicable legal-request process.</p>
                        </div>
                    </section>

                    <section id="section-29" className="mb-6">
                        <h2 className="text-[14px] font-bold mb-2 text-gray-800">29. No Guarantee of Monitoring</h2>
                        <div className="space-y-2 text-gray-700 text-[12px]">
                            <p>Chaa Chingg works to identify and address violations, but we cannot guarantee that all content will be reviewed before it appears or that every violation will be detected immediately.</p>
                            <p>Users remain responsible for the content they create, upload, send, and share.</p>
                            <p>The presence of content on Chaa Chingg does not mean that Chaa Chingg endorses, verifies, or approves it.</p>
                        </div>
                    </section>

                    <section id="section-30" className="mb-6">
                        <h2 className="text-[14px] font-bold mb-2 text-gray-800">30. Changes to These Guidelines</h2>
                        <div className="space-y-2 text-gray-700 text-[12px]">
                            <p>Online risks, technology, laws, and Chaa Chingg's features may change over time. We may update these Community Guidelines to address new conduct, safety concerns, legal requirements, or product features.</p>
                            <p>When appropriate, we will provide notice of material changes and update the effective date shown at the beginning of these Guidelines.</p>
                            <p>Continued use of Chaa Chingg after updated Guidelines take effect means that users must follow the updated version.</p>
                        </div>
                    </section>

                    <section id="section-31" className="pb-6">
                        <h2 className="text-[14px] font-bold mb-2 text-gray-800">31. Contact Us</h2>
                        <div className="space-y-2 text-gray-700 text-[12px]">
                            <p>Questions, reports, and appeals relating to these Community Guidelines may be submitted through Chaa Chingg's in-app reporting tools or directed to:</p>
                            {bulletList(contactList)}
                        </div>
                    </section>
                </main>
            </div>
        </div>
    );
}
